import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "shared/contexts/AuthContext";
import { startLogin } from "shared/auth/auth";
import { sendGTMEvent } from "shared/services/GTM";
import {
  AlertsApiError,
  deleteAlert,
  getAlerts,
  toggleAlertStatus,
} from "shared/services/alerts/alerts.service";
import { getPlatformType } from "shared/utils/platform";
import type { Alert } from "shared/services/alerts/alerts.types";

export type AlertsFeedback = {
  message: string;
  type: "success" | "error";
};

const PAGE_SIZE = 10;

/**
 * Estado e ações da lista de alertas do usuário (paginação, ativar/desativar
 * e excluir). Não conhece formulário: a criação fica em outra tela.
 */
export const useAlertsList = () => {
  const { user, refreshSession } = useAuth();
  const userId = user?.id;

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busyAlertIds, setBusyAlertIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<AlertsFeedback | null>(null);

  const viewedRef = useRef(false);

  const setBusy = (id: string, busy: boolean) =>
    setBusyAlertIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const loadPage = useCallback(
    async (pageNumber: number) => {
      if (!userId) return;

      const isFresh = pageNumber === 1;
      if (isFresh) {
        if (!viewedRef.current) setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const result = await getAlerts(userId, pageNumber, PAGE_SIZE);

        if (isFresh) {
          setAlerts(result.alerts);
          if (!viewedRef.current) {
            sendGTMEvent("alertsListView", {
              platform: getPlatformType(),
              totalLoaded: result.alerts.length,
            });
            viewedRef.current = true;
          }
        } else {
          setAlerts((prev) => {
            const ids = new Set(prev.map((a) => a.id));
            return [...prev, ...result.alerts.filter((a) => !ids.has(a.id))];
          });
        }

        setPage(pageNumber);
        setHasMore(result.hasMore);
        setLoadError(false);
      } catch (error) {
        if (error instanceof AlertsApiError && error.status === 401) {
          const refreshed = await refreshSession();
          if (!refreshed) {
            startLogin({ source: "alerts", replaceCurrentPage: true });
            return;
          }
        }
        setLoadError(true);
      } finally {
        if (isFresh) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [userId, refreshSession],
  );

  useEffect(() => {
    if (userId) loadPage(1);
  }, [userId, loadPage]);

  const toggle = async (alert: Alert) => {
    setBusy(alert.id, true);
    const nextState = !alert.isStopped;

    try {
      await toggleAlertStatus({
        id: alert.id,
        userID: userId || "",
        isStopped: nextState,
      });
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === alert.id ? { ...a, isStopped: nextState } : a,
        ),
      );
      setFeedback({
        message: nextState ? "Alerta desativado." : "Alerta ativado.",
        type: "success",
      });
      sendGTMEvent("alertManage", {
        platform: getPlatformType(),
        action: nextState ? "deactivate" : "activate",
        result: "success",
        alertId: alert.id,
      });
    } catch {
      setFeedback({
        message:
          "Não foi possível alterar o alerta. Tente novamente em instantes.",
        type: "error",
      });
      sendGTMEvent("alertManage", {
        platform: getPlatformType(),
        action: nextState ? "deactivate" : "activate",
        result: "error",
        alertId: alert.id,
      });
    } finally {
      setBusy(alert.id, false);
    }
  };

  const remove = async (alert: Alert) => {
    setBusy(alert.id, true);

    try {
      await deleteAlert(alert.id);
      setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
      setFeedback({ message: "Alerta excluído.", type: "success" });
      sendGTMEvent("alertManage", {
        platform: getPlatformType(),
        action: "delete",
        result: "success",
        alertId: alert.id,
      });
    } catch {
      setFeedback({
        message:
          "Não foi possível excluir o alerta. Tente novamente em instantes.",
        type: "error",
      });
      sendGTMEvent("alertManage", {
        platform: getPlatformType(),
        action: "delete",
        result: "error",
        alertId: alert.id,
      });
    } finally {
      setBusy(alert.id, false);
    }
  };

  return {
    alerts,
    page,
    hasMore,
    loading,
    loadingMore,
    loadError,
    busyAlertIds,
    feedback,
    setFeedback,
    loadPage,
    toggle,
    remove,
  };
};
